import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { TerritoryResolution } from '@buzzmap/shared';
import { useUserPreferences } from './useUserPreferences';
import { useEventsContextOptional } from '../contexts/EventsContext';
import { useDiscoveryOptional } from '../contexts/discoveryStore';
import { isTerritoryPath, parsePresetRoute, territoryRouteKey } from '../routes/territoryPaths';
import { parseCategorieParam } from '../utils/categoryLinks';

export interface TerritoryRouteResolution {
  /** Full resolution from `/api/territory`, or `null` on a non-territory
   *  route, before the fetch settles, or after any failure (404, network
   *  error, malformed body). */
  resolution: TerritoryResolution | null;
  /** True once this route's resolution attempt has finished one way or
   *  another (including "there was nothing to resolve"). Callers use this
   *  to know when it's safe to remove the SSR shell without a content
   *  flash. */
  settled: boolean;
}

interface TerritoryRouteResolutionOptions {
  /**
   * False when the caller already holds a resolution (App's
   * `TerritoryRouteElement` owns it for period presets) — the hook then does
   * nothing at all: no fetch, no override, no pending-gate clear.
   */
  enabled?: boolean;
}

/**
 * Resolves the current territory route and exposes it to `HomePage` /
 * `EsploraPage`.
 *
 * Two modes (T010-FE), selected by `DiscoveryProvider`:
 * - **v2** (registry `ready === true`): `DiscoveryProvider` owns resolution
 *   (cached per territory/version) and the URL is the sole authority. This hook
 *   is a thin delegate — it does NOT fetch and does NOT apply a session
 *   override; it just returns the provider's resolution/settled so the pages'
 *   shell logic keeps working unchanged.
 * - **legacy** (pre-activation, capabilities loading/error, or no provider —
 *   the pre-existing behaviour): matches the route, resolves it via
 *   `/api/territory`, and applies the returned `location` as a session-only
 *   override (C1 — `setLocationOverride`, NEVER `setLocationChoice`,
 *   `localStorage`, or the user's account). It also applies the period's date
 *   window and the path/`?categorie=` selection, then clears the pending gate.
 *   On any failure the caller stays on its normal (non-territory) rendering:
 *   there is no dedicated error screen.
 *
 * The legacy leg is keyed on `territoryRouteKey` (pathname + effective
 * period), so a UTM change or a `?categorie=`-only change does NOT refire it,
 * and an in-flight request for a path the user has since navigated away from
 * is aborted via `AbortController` so a stale response can never overwrite a
 * newer override. Two guards keep the pre-activation window byte-identical to
 * HEAD (context-task-T010.md W4/W-A):
 * - while the capabilities bootstrap is still in flight (`mode === 'loading'`)
 *   the legacy leg HOLDS instead of firing a headerless request that would
 *   426 at activation;
 * - in `upgrade-required` it is inert (no fetch, no gate clear) because the
 *   upgrade state owns the session.
 * A legacy fetch answered 426 renegotiates through
 * `discovery.reportUpgradeRequired` instead of stranding the tab in legacy.
 *
 * Does NOT remove the SSR shell itself (T012-FE) — that decision now belongs
 * to each caller (`HomePage`, `EsploraPage`), which may need to wait on more
 * than just this hook's `settled` flag.
 */
export function useTerritoryRouteResolution(
  options: TerritoryRouteResolutionOptions = {}
): TerritoryRouteResolution {
  const discovery = useDiscoveryOptional();
  const mode = discovery?.mode;
  const v2 = mode === 'v2';
  // W-A: in `upgrade-required` the provider is inert and the upgrade state owns
  // the session — this legacy path must NOT fire a headerless fetch (426 at
  // activation) nor clear the pending gate (which would let the feed attempt a
  // stored-geography request). Hold `settled:false` and do nothing.
  const upgradeRequired = mode === 'upgrade-required';
  // W4: while the capabilities bootstrap is still in flight, the legacy path
  // does NOT fetch — otherwise a cold territory entry would fire one
  // headerless request (which 426s at activation) plus the v2 request. Waiting
  // for the gate to settle yields exactly one resolution fetch in either mode.
  const capsPending = mode === 'loading';
  // T016-FE: the 426 renegotiation signal, read inside the effect below (and
  // listed in its deps so a renegotiation re-runs this effect — see the note
  // above the dependency array).
  const upgradeReport = discovery?.reportUpgradeRequired;

  const { pathname, search } = useLocation();
  const { setLocationOverride, clearLocationResolutionPending } = useUserPreferences();
  const events = useEventsContextOptional();
  const enabled = options.enabled ?? true;
  const searchRef = useRef(search);
  searchRef.current = search;
  const key = territoryRouteKey(pathname, search);
  const [state, setState] = useState<TerritoryRouteResolution & { key: string }>({
    key,
    resolution: null,
    settled: false,
  });

  useEffect(() => {
    if (!enabled) return;
    // v2: DiscoveryProvider owns resolution; the legacy fetch/override path is
    // inert so the two mechanisms never double-apply. upgrade-required: inert
    // too (W-A) — no headerless fetch, no gate clear.
    if (v2 || upgradeRequired) return;

    setState({ key, resolution: null, settled: !isTerritoryPath(pathname) });
    if (!isTerritoryPath(pathname)) return;

    const preset = parsePresetRoute(pathname, search);
    const path =
      preset?.segments.join('/') ?? pathname.replace(/\/+$/, '').split('/').slice(2).join('/');
    const params = new URLSearchParams({ path });
    if (preset?.periodSlug && !preset.periodInPath) params.set('quando', preset.periodSlug);

    // W4: hold (settled stays false) rather than fire a request we would have
    // to repeat once the gate is known. The UserContext render-gate already
    // holds territory feeds from first render, so the SSR shell covers the gap.
    if (capsPending) return;

    const controller = new AbortController();

    fetch(`/api/territory?${params.toString()}`, { signal: controller.signal })
      .then((response) => {
        // T016-FE: a headerless legacy fetch answered 426 means the epoch
        // moved under this open tab (mid-session activation). Renegotiate
        // through the provider instead of leaving the session stranded in
        // legacy until a manual reload.
        if (response.status === 426) upgradeReport?.();
        return response.ok ? (response.json() as Promise<TerritoryResolution>) : null;
      })
      .then((result) => {
        if (controller.signal.aborted || !result) return;
        // `events` is null only when the caller renders this hook outside an
        // EventsProvider (isolated probes/tests) — there is no feed to update.
        if (result.period) events?.setCustomDateRange(result.period.from, result.period.to);
        if (preset?.category) {
          events?.setCategories([preset.category.categoryId]);
        } else if (result.period || new URLSearchParams(searchRef.current).has('categorie')) {
          events?.setCategories(parseCategorieParam(searchRef.current));
        }
        setLocationOverride(result.location);
        setState({ key, resolution: result, settled: false });
      })
      .catch(() => {
        // Aborted (path changed / unmounted), network error, 404, or a
        // malformed body: the shell stays and the page uses its own fallback.
      })
      .finally(() => {
        // Only settle (and clear the pending gate) for the request that
        // actually completed: a stale request whose controller was aborted
        // must not clear a gate a newer run already owns.
        if (controller.signal.aborted) return;
        setState((previous) => ({ ...previous, settled: true }));
        clearLocationResolutionPending();
      });

    return () => controller.abort();
    // `upgradeReport` is a stable callback; it is listed so a 426 renegotiation
    // that flips the published mode re-runs this effect into the v2/upgrade
    // branch (the mode signal reaches this hook one render late, since
    // `publishDiscoveryMode` runs in an effect above it). `pathname`/`search`
    // are covered by `key` — UTM and category-only query changes must NOT
    // refire the resolution (upstream contract), so they stay out on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, v2, upgradeRequired, capsPending, upgradeReport]);

  // v2: the provider is the authority for this route.
  if (v2 && discovery) {
    return { resolution: discovery.resolution, settled: discovery.settled };
  }
  // W-A: upgrade-required holds the session — no resolution, never settled.
  if (upgradeRequired) {
    return { resolution: null, settled: false };
  }
  // A key change (pathname/period) resets the visible state immediately so a
  // stale response can never paint the new route.
  return state.key === key
    ? { resolution: state.resolution, settled: state.settled }
    : { resolution: null, settled: false };
}
